import { useEffect } from 'react'
import type { ReactNode } from 'react'
import { useSelector } from '@/libs/state'
import { session$ } from '@/features/auth/store'
import { useIntlayerLocaleAdapter } from '@/features/i18n/adapters/intlayer-locale'
import { makeSetLocaleUseCase } from '@/features/i18n/application/set-locale'
import { makeSetTheme } from '@/features/theme/application/set-theme'
import { theme$ } from '@/features/theme/store'
import { themePort } from '@/features/theme/wiring'
import { getUserPreferences } from '../application/get-user-preferences'
import { userPreferences$ } from '../store'
import { userPreferencesPort } from '../wiring'

type Props = { children: ReactNode }

const applyTheme = makeSetTheme(themePort)

const UserPreferencesProvider = ({ children }: Props) => {
	const userId = useSelector(() => session$.user.id.get())
	const localeAdapter = useIntlayerLocaleAdapter()

	useEffect(() => {
		if (!userId) return

		let cancelled = false
		const applyPreferences = async (): Promise<void> => {
			try {
				const preferences = await getUserPreferences(userPreferencesPort)
				if (cancelled) return
				userPreferences$.set(preferences)
				theme$.set(preferences.theme)
				applyTheme(preferences.theme)
				makeSetLocaleUseCase(localeAdapter)(preferences.locale)
			} catch {}
		}

		void applyPreferences()

		return () => {
			cancelled = true
		}
	}, [userId, localeAdapter])

	return <>{children}</>
}

export { UserPreferencesProvider }
